/**
 * Custom error class for HTTP responses.
 */
export class HttpError extends Error {
  status: number
  data: unknown

  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.data = data
  }
}

// Shared state to track token refresh status and queue concurrent requests
let isRefreshing = false
let refreshPromise: Promise<void> | null = null

/**
 * Helper to handle 401 refreshes shared between fetch wrappers.
 * Attempts to refresh the token. If successful, the promise resolves.
 * If failed, it throws, allowing the caller to handle the logout/error.
 */
async function handle401Refresh(): Promise<void> {
  // Lazy-import to avoid circular dependencies
  const { useAuthStore } = await import('@/stores/auth')
  const auth = useAuthStore()

  if (!isRefreshing) {
    isRefreshing = true
    refreshPromise = auth
      .refreshToken()
      .catch((error) => {
        // Refresh failed (e.g. refresh token also expired)
        auth.logout()
        throw error
      })
      .finally(() => {
        isRefreshing = false
        refreshPromise = null
      })
  }

  if (refreshPromise) {
    await refreshPromise
  }
}

/**
 * Esegue una chiamata API autenticata aggiungendo il token Bearer all'header Authorization.
 * @param {string} endpoint - L'endpoint API da chiamare.
 * @param {RequestInit} [options] - Le opzioni della richiesta fetch.
 * @param {boolean} [rawResponse=false] - Se true, restituisce la risposta raw invece di JSON.
 * @returns {Promise<unknown>} La risposta JSON dal backend o la risposta raw.
 */
export async function authFetch(
  endpoint: string,
  options: RequestInit = {},
  rawResponse = false,
): Promise<unknown> {
  const baseURL = import.meta.env.VITE_API_URL
  const apiKey = import.meta.env.VITE_API_KEY

  if (!baseURL || !apiKey) {
    throw new Error(
      "Le variabili d'ambiente VITE_API_URL o VITE_API_KEY non sono configurate nel file .env.",
    )
  }

  const finalURL = new URL(endpoint.startsWith('/') ? endpoint.substring(1) : endpoint, baseURL)
    .href

  // Read latest tokens from storage
  const tokensJson = localStorage.getItem('tokens')
  const tokens = tokensJson ? JSON.parse(tokensJson) : {}

  const headers: Record<string, string> = {
    //  'x-api-key': apiKey,
    ...(options.headers as Record<string, string>),
  }

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  if (tokens.access) {
    headers['Authorization'] = `Bearer ${tokens.access}`
  }

  const response = await fetch(finalURL, {
    ...options,
    headers,
  })

  if (!response.ok) {
    // 401 Interceptor Logic
    if (response.status === 401) {
      try {
        await handle401Refresh()
        // Retry the original request recursively
        return await authFetch(endpoint, options, rawResponse)
      } catch (error) {
        console.error('Token refresh failed during authFetch retry:', error)
        // Fall through to throw the original 401 or the refresh error
      }
    }

    const errorData = await response.json().catch(() => ({ message: 'Errore nella richiesta API' }))
    throw new HttpError(
      errorData.error || errorData.detail || errorData.message || 'Errore di rete',
      response.status,
      errorData,
    )
  }

  if (rawResponse) {
    return response
  }

  // Handle 204 No Content response gracefully
  if (response.status === 204) {
    return null
  }

  return response.json()
}

/**
 * Esegue una chiamata API per la funzionalità di query NLP.
 * Utilizza un endpoint e una chiave API specifici per il server.
 * @param {string} endpoint - L'endpoint API da chiamare (es. '/query').
 * @param {RequestInit} [options] - Le opzioni della richiesta fetch.
 * @returns {Promise<Response>} La risposta raw dal backend.
 */
export async function queryFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  // the same as Django's
  const baseURL = import.meta.env.VITE_API_URL
  // const apiKey = import.meta.env.VITE_API_KEY

  if (!baseURL) {
    throw new Error(
      "Le variabili d'ambiente VITE_API_URL o VITE_API_KEY non sono configurate nel file .env.",
    )
  }

  const finalURL = new URL(endpoint.startsWith('/') ? endpoint.substring(1) : endpoint, baseURL)
    .href

  const headers: Record<string, string> = {
    // 'X-API-KEY': apiKey,
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  const tokensJson = localStorage.getItem('tokens')
  const tokens = tokensJson ? JSON.parse(tokensJson) : {}

  if (tokens.access) {
    headers['Authorization'] = `Bearer ${tokens.access}`
  }

  const response = await fetch(finalURL, {
    ...options,
    headers,
  })

  // 401 Interceptor Logic for queryFetch
  if (response.status === 401) {
    try {
      await handle401Refresh()
      // Retry the original request recursively
      return await queryFetch(endpoint, options)
    } catch (error) {
      console.error('Token refresh failed during queryFetch retry:', error)
      // Return original 401 response if refresh fails, letting the caller handle the error state
      return response
    }
  }

  return response
}

/**
 * Esegue una chiamata API per la funzionalità di query NLP.
 * Utilizza un endpoint e una chiave API specifici per il server.
 * @param {string} endpoint - L'endpoint API da chiamare (es. '/query').
 * @param {RequestInit} [options] - Le opzioni della richiesta fetch.
 * @returns {Promise<Response>} La risposta raw dal backend.
 */
export async function queryFetchLocalServer(
  endpoint: string,
  options: RequestInit = {},
): Promise<Response> {
  const baseURL = 'http://localhost:8001/'
  // const apiKey = import.meta.env.VITE_API_KEY

  if (!baseURL) {
    throw new Error(
      "Le variabili d'ambiente VITE_API_URL o VITE_API_KEY non sono configurate nel file .env.",
    )
  }

  const finalURL = new URL(endpoint.startsWith('/') ? endpoint.substring(1) : endpoint, baseURL)
    .href

  const headers: Record<string, string> = {
    // 'X-API-KEY': apiKey,
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  // Not applying refresh logic here as local server might not use the same auth flow
  // or is strictly dev-only.
  return await fetch(finalURL, {
    ...options,
    headers,
  })
}

/**
 * Downloads a file from the given endpoint.
 */
export async function downloadFile(endpoint: string, params: URLSearchParams) {
  try {
    const response = (await authFetch(`${endpoint}?${params.toString()}`, {}, true)) as Response
    const contentDisposition = response.headers.get('content-disposition')
    let filename = 'download.xlsx'
    if (contentDisposition) {
      let match = contentDisposition.match(/filename="([^"]+)"/)
      if (!match) match = contentDisposition.match(/filename=([^;]+)/)
      if (match && match[1]) filename = match[1].trim()
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading file:', error)
    throw error
  }
}
